import { format } from "date-fns";
import { useParams } from "react-router";
import {
  DataDumpAggregateReportsType,
  DataDumpType,
  PnlCategoriesEnum,
  PnlCategoriesWithTags,
  ReportParams,
} from "../../types";
import {
  ONE_MINUTE_OF_MS,
  ONE_HOUR_OF_MS,
  FORMAT_MONTH_DAY,
  ONE_DAY_OF_MS,
  NUM_DAYS_IN_WEEK,
} from "reports/utils/timeUtils";
import { useFetchReportsProcessGroup } from "../Dashboard/dashboardUtils";
import {
  MachineBreakdownInputProps,
  MachineBreakdownOutputProps,
} from "./GenericCharts/MetricAndPnLBreakdowns/metricAndPnLBreakdownsTypes";
import { getPnlTimes } from "./GenericCharts/PnLChart/pnlChartUtils";
import { useLocation } from "react-router";
import queryString from "query-string";
import {
  BreakdownWithTagsType,
  PnlBreakdown,
} from "../../getters/pnlBreakdownGetter/pnlBreakdownTypes";
import { cloneDeep, max, uniq } from "lodash";
import { useMidgardProcesses } from "../Dashboard/useFetchProcesses";
import { TimeRangeFinite } from "types/types";
import useQueryString from "utils/customHooks/useQueryString";

/**
 * Get ge and lt from url path
 *
 * @return {*}  {{ ge: number; lt: number }}
 */
export const useGeAndLtFromUrl = (): { ge: number; lt: number } => {
  const location = useLocation();
  const values = queryString.parse(location.search) as {
    ge?: string;
    lt?: string;
  };
  let ge = values?.ge ? parseInt(values?.ge, 10) : 0;
  let lt = values?.lt ? parseInt(values?.lt, 10) : 0;

  // Some browsers convert lt to '<='
  // This check fixes that issue to get a valid lt
  if (values?.ge && values.ge.includes("<=") && !values?.lt) {
    ge = parseInt(values.ge.split("<=")[0], 10);
    lt = parseInt(values.ge.split("<=")[1], 10);
  }

  return { ge, lt };
};

/**
 * Get average rate in milliseconds
 *
 * @param {{
 *   dataDumpAggregate: DataDumpAggregateReportsType;
 *   rateTime: number;
 * }} {
 *   dataDumpAggregate,
 *   rateTime
 * }
 * @return {*}
 */
export const getAverageRate = ({
  dataDumpAggregate,
  rateTime,
}: {
  dataDumpAggregate: DataDumpAggregateReportsType;
  rateTime: number;
}): number => {
  const { netOutput, runningTime } = dataDumpAggregate;
  return netOutput && runningTime ? (netOutput / runningTime) * rateTime : 0;
};

/**
 * Get unit for rate, hours vs minutes
 *
 * @param {number} rateValue
 * @return {*}  {string}
 */
export const getRateUnit = (rateValue: number): string => {
  return rateValue === ONE_MINUTE_OF_MS
    ? "BPM"
    : rateValue === ONE_HOUR_OF_MS
      ? "BPH"
      : "";
};

/**
 * Get major stop time
 *
 * @param {{
 *   scheduledTime: number;
 *   oee: number;
 *   minorStopTime: number;
 * }} {
 *   scheduledTime,
 *   oee,
 *   minorStopTime
 * }
 * @return {*} number
 */
export const getMajorStopTime = ({
  scheduledTime,
  oee,
  minorStopTime,
}: {
  scheduledTime: number;
  oee: number;
  minorStopTime: number;
}): number => {
  const totalLosses = (1 - oee / 100) * scheduledTime;
  return totalLosses - minorStopTime;
};

type SegmentBreakdownByCategory = {
  [category: string]: { [name: string]: { number: number; duration: number } };
};

/**
 * Iterates through dataDumps and keeps a count and duration of each segment per category (and per tag)
 *
 * @param {DataDumpType[]} dataDumps
 * @returns {{ [category: string]: { [name: string]: { number: number; duration: number } } }}
 */
export const getNumberAndDurationOfSegmentsByCategory = (
  dataDumps: DataDumpType[]
): SegmentBreakdownByCategory => {
  return dataDumps.reduce((accum, datum) => {
    const category = datum.pnlCategory || "category";
    const duration = Number(datum.lt) - Number(datum.ge);
    const tagName = datum?.tagName;

    accum[category] = !accum[category]
      ? // If category has not yet been added to the return, initialize total
        { total: { number: 1, duration } }
      : // Add to existing category total
        {
          ...accum[category],
          total: {
            number: accum[category].total?.number + 1,
            duration: accum[category].total?.duration + duration,
          },
        };

    // If tagName exists in segment
    if (tagName) {
      accum[category] = !accum[category][tagName]
        ? // If tagName has not yet been added to the return
          {
            ...accum[category],
            [tagName]: { number: 1, duration },
          }
        : // Add to existing tagName total
          {
            ...accum[category],
            [tagName]: {
              number: accum[category][datum.tagName]?.number + 1,
              duration: accum[category][datum.tagName]?.duration + duration,
            },
          };
    }
    return accum;
  }, {});
};

type SegmentCategoryType = {
  duration: number;
  ge: number;
  processName: string;
  date: string;
};
export type SegmentCategoryOrderType = SegmentCategoryType & { order: number };
/**
 * Iterates through dataDumps and gets duration, order, ge, date and machine of each segment for the given category
 *
 * @param {{
 *   dataDumps: DataDumpType[];
 *   category: string;
 * }} {
 *   dataDumps,
 *   category
 * }
 * @returns {SegmentCategoryOrderType[]}
 */
export const getSegmentsForCategoryInOrder = ({
  dataDumps,
  category,
}: {
  dataDumps: DataDumpType[];
  category: string;
}): SegmentCategoryOrderType[] => {
  // get data dumps only for category supplied and that are more than 1 minute
  const sortedDataDumpsForCategory = dataDumps.filter(
    ({ pnlCategory, ge, lt }) =>
      pnlCategory === category &&
      (Number(lt) - Number(ge)) / ONE_MINUTE_OF_MS > 1
  );

  // group segments that are on the same day and by the same machine
  const dataGroupedByDateAndMachine = sortedDataDumpsForCategory.reduce(
    (accum: { [key: string]: SegmentCategoryType }, obj: DataDumpType) => {
      const { processName, ge, lt } = obj;
      const date = format(parseInt(ge, 10), FORMAT_MONTH_DAY);
      const duration = Number(lt) - Number(ge);
      const key = date + "-" + obj.processName;

      accum[key] = {
        ...(accum[key] || {}),
        duration: (accum[key]?.duration || 0) + duration,
        ge: Number(ge),
        date,
        processName,
      };

      return accum;
    },
    {}
  );

  // return ordered data
  return Object.entries(dataGroupedByDateAndMachine)
    .map(([, { duration, ge, date, processName }], index) => {
      return {
        duration,
        ge,
        date,
        processName,
        order: index + 1,
      };
    })
    .sort((a, b) => (a.ge > b.ge ? 1 : a.ge < b.ge ? -1 : 0));
};

/**
 * Iterates through dataDumps and keeps a count and duration of each segment for a specific category and machine
 *
 * @param {DataDumpType[]} dataDumps
 * @returns {{ [category: string]: { [name: string]: { number: number; duration: number } } }}
 */
export const getNumberAndDurationOfSegmentsByMachine = ({
  dataDumps,
  category,
}: {
  dataDumps: DataDumpType[];
  category: string;
}) => {
  const dataDumpsForCategoryAndMachine = dataDumps.filter(
    datum => datum.pnlCategory === category
  );

  return dataDumpsForCategoryAndMachine.reduce((accum, datum) => {
    const machineName = datum.processName;
    const duration = Number(datum.lt) - Number(datum.ge);
    accum[machineName] = !accum[machineName]
      ? { number: 1, duration }
      : {
          number: accum[machineName].number + 1,
          duration: accum[machineName].duration + duration,
        };
    return accum;
  }, {});
};

/**
 * Iterates through dataDumps and keeps a list of skus for a specific  machine
 *
 * @param {DataDumpType[]} dataDumps
 * @returns {{ [category: string]: { [name: string]: { number: number; duration: number } } }}
 * @returns {skuName: string; netOutput: number;}
 */
export const getSkuCountByMachemetryIds = ({
  dataDumps,
  machemetryIds,
}: {
  dataDumps: DataDumpType[];
  machemetryIds: string[];
}) => {
  const dataDumpsForMachine = dataDumps.filter(datum =>
    machemetryIds.includes(datum.machemetryId)
  );

  const skuData = dataDumpsForMachine.reduce((accum, datum) => {
    const skuName = datum?.skuName;
    if (skuName) {
      const netOutput = datum.netOutput || 0;
      const skuCode = datum.sku;
      accum[skuName] = !accum[skuName]
        ? { netOutput, skuCode }
        : {
            ...accum[skuName],
            netOutput: accum[skuName].netOutput + netOutput,
            skuCode,
          };
    }
    return accum;
  }, {});

  return Object.entries(skuData).map(skuDatum => {
    return {
      skuName: skuDatum[0],
      netOutput: (skuDatum[1] as { netOutput: number }).netOutput,
      skuCode: (skuDatum[1] as { skuCode: string }).skuCode,
    };
  });
};

/**
 * Iterates through the list of datadumps and counts the number of unknown major and minor stops,
 * and unknown major and minor stop times
 *
 * Calculated based on:
 * minorStop = downtime < minorStopThresholdMs
 * majorStop = downtime >= minorStopThresholdMs
 *
 * @param {{
 *   dataDumps: DataDumpType[];
 *   minorStopThresholdMs: number;
 * }} {
 *   dataDumps,
 *   minorStopThresholdMs
 * }
 * @return {*}  {{
 *   numMinorStops: number;
 *   numMajorStops: number;
 *   minorStopTime: number;
 *   majorStopTime: number;
 * }}
 */
export const getNumberOfUnknownStopsAndDuration = ({
  dataDumps,
  minorStopThresholdMs,
}: {
  dataDumps: DataDumpType[];
  minorStopThresholdMs: number;
}): {
  numMinorStops: number;
  numMajorStops: number;
  minorStopTime: number;
  majorStopTime: number;
} => {
  return dataDumps.reduce(
    (accum, datum) => {
      const duration = Number(datum.lt) - Number(datum.ge);

      // we just want unknown segments
      return datum.pnlCategory === "unknown"
        ? duration < minorStopThresholdMs
          ? {
              ...accum,
              numMinorStops: accum.numMinorStops + 1,
              minorStopTime: accum.minorStopTime + duration,
            }
          : {
              ...accum,
              numMajorStops: accum.numMajorStops + 1,
              majorStopTime: accum.majorStopTime + duration,
            }
        : accum;
    },
    { numMinorStops: 0, numMajorStops: 0, minorStopTime: 0, majorStopTime: 0 }
  );
};

export const getMachineBreakDownProps = ({
  machemetryIds,
  pnlBreakdownsByProcess,
  pnlBreakdownsBySKU,
  categoryPropsArr,
  flightDashboardLinks,
  getMachineBreakDownPropsToOverride,
}: MachineBreakdownInputProps): MachineBreakdownOutputProps[] | null => {
  return machemetryIds.map(machemetryId => {
    const skuData =
      pnlBreakdownsBySKU?.filter(
        skuDatum => skuDatum?.machemetryIds?.[0] === machemetryId
      ) ||
      pnlBreakdownsBySKU?.[0] ||
      [];

    const pnlBreakdown =
      pnlBreakdownsByProcess.find(
        ({ machemetryIds: ids }) => ids[0] === machemetryId
      ) || pnlBreakdownsByProcess?.[0];
    const processName = pnlBreakdown.processNames[0];

    const flightDashboardLink =
      flightDashboardLinks.find(
        mapping => mapping.machemetryId === machemetryId
      )?.dashboardLink ?? null;

    const machineBreakdownPropsToOverride = getMachineBreakDownPropsToOverride
      ? getMachineBreakDownPropsToOverride(machemetryId)
      : {};

    const pnlTimes = getPnlTimes({ data: pnlBreakdown, categoryPropsArr });
    return {
      machemetryId,
      processName,
      pnlBreakdown,
      pnlTimes,
      skuData,
      flightDashboardLink,
      ...machineBreakdownPropsToOverride,
    };
  });
};

/**
 * Get processGroupName and machemetryIds from url processGroup
 *
 * @param {string[]} [machemetryIdsToExclude]
 * @return {*}
 */
export const useProcessGroupNameAndMachemetryIds = ({
  machemetryIdsToExclude,
  defaultProcessGroupName = "All",
}: {
  machemetryIdsToExclude?: string[];
  defaultProcessGroupName?: string;
} = {}) => {
  const { processGroupName } = useParams<ReportParams>();

  const processGroupNameToUse = processGroupName
    ? decodeURIComponent(processGroupName)
    : defaultProcessGroupName;

  const processGroups = useFetchReportsProcessGroup();
  const processGroup = processGroups?.find(
    group =>
      group.name.toLowerCase().trim() ===
      processGroupNameToUse.toLowerCase().trim()
  );

  const processes = useMidgardProcesses();
  // Dealing with single process
  if (!processGroup) {
    const process = processes?.find(
      ({ name }) => name === processGroupNameToUse
    );
    return {
      processGroupName: process?.name ?? "",
      machemetryIds: process?.taps?.[0]?.machemetryId
        ? [process.taps[0].machemetryId]
        : [],
    };
  }

  const allProcesses = [...(processGroup?.processes || [])];
  const machemetryIds = uniq(
    allProcesses
      .map(({ taps }) => taps[0].machemetryId)
      // Filter machines from report
      .filter(machemetryId => !machemetryIdsToExclude?.includes(machemetryId))
  );

  const processGroupDisplayName = processGroup?.name;

  return { processGroupName: processGroupDisplayName, machemetryIds };
};

/**
 * Get the list of machemetry ID's for all process groups
 * @param {string[]}[processGroupList]
 * @returns [{processGroupName, [machemetryIds] }]
 */
export const useProcessGroupAndMachemetryIdList = ({
  processGroupList,
}: {
  processGroupList: string[];
}) => {
  const processGroups = useFetchReportsProcessGroup();
  const processGroupsToUse = processGroups?.filter(processGroup =>
    processGroupList.includes(processGroup.name)
  );

  return processGroupsToUse?.map(processGroup => {
    const machemetryIds = [...(processGroup?.processes || [])]
      .sort((a, b) =>
        a.description.localeCompare(b.description, "en", {
          numeric: true,
        })
      )
      .map(({ taps }) => taps[0].machemetryId);

    return { processGroupName: processGroup.name, machemetryIds };
  });
};
/**
 * Formats tag names by removing sub strings in subStringsToRemove
 *
 * @param {{
 *   pnlBreakdown: PnlBreakdown;
 *   prefixesToRemove: string[];
 * }} {
 *   pnlBreakdown,
 *   prefixesToRemove
 * }
 */
export const removePrefixesFromTagName = ({
  pnlBreakdown,
  prefixesToRemove,
}: {
  pnlBreakdown: PnlBreakdown;
  prefixesToRemove: string[];
}) => {
  const prefixesToRemoveRegex = new RegExp(
    "^(" + prefixesToRemove.join("|") + ")",
    "g"
  );

  const pnlBreakdownToModify = cloneDeep(pnlBreakdown);

  PnlCategoriesWithTags.map(category => {
    const dataForCategory = pnlBreakdownToModify?.[
      category as string
    ] as BreakdownWithTagsType;
    const formattedTagNames = dataForCategory.tags.map(tag => ({
      ...tag,
      tagName: tag.tagName.replace(prefixesToRemoveRegex, ""),
    }));

    dataForCategory.tags = formattedTagNames;
  });

  return pnlBreakdownToModify;
};

/**
 * Get longest duration of tags within the given categories
 *
 * @export
 * @param {{
 *   pnlBreakdown: PnlBreakdown;
 *   categories: PnlCategoriesEnum[];
 * }} {
 *   pnlBreakdown,
 *   categories,
 * }
 * @return {*}
 */
export function getMaxTagDurationMs({
  pnlBreakdown,
  categories,
}: {
  pnlBreakdown: PnlBreakdown;
  categories: PnlCategoriesEnum[];
}) {
  return max(
    categories.map(category =>
      max(
        (pnlBreakdown[category] as BreakdownWithTagsType).tags?.map(
          tag => tag.durationMs
        )
      )
    )
  );
}

export function useIsWeekly() {
  const { ge, lt } = useGeAndLtFromUrl();
  return (lt - ge) / ONE_DAY_OF_MS >= NUM_DAYS_IN_WEEK;
}

export function useIs24Hours() {
  const { ge, lt } = useGeAndLtFromUrl();
  return (lt - ge) / ONE_HOUR_OF_MS === 24;
}
export function useIsShiftly() {
  const { ge, lt } = useGeAndLtFromUrl();
  const hours = (lt - ge) / ONE_HOUR_OF_MS;
  return hours > 1 && hours <= 22;
}

export function useGetWorkScheduleForAProcess(machemetryId: string) {
  const all_processes = useMidgardProcesses();
  const target_process = all_processes?.find(
    process => process.taps[0].machemetryId === machemetryId
  );

  return target_process?.workScheduleConfig.id;
}

export function useFocusRangeFromQuery(): TimeRangeFinite | null {
  const parsedQuery = useQueryString();
  const focusRangeFromQuery =
    parsedQuery.ge && parsedQuery.lt
      ? {
          ge: Number(parsedQuery.ge),
          lt: Number(parsedQuery.lt),
        }
      : null;
  return focusRangeFromQuery;
}
